'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Lets deep detail pages feed a real, data-derived title for their dynamic
 * path segment (a slug or id) into the single shared `AppBreadcrumbBar`, so the
 * breadcrumb shows "Global Dance Open Finals 2026" instead of the humanized
 * slug "Gdo Finals 2026". The bar reads the map; pages register via
 * `<SetBreadcrumbTitle segment={slug} title={record.title} />`. Segments no
 * page registers fall back to `AppBreadcrumbs`' pathname humanization.
 */
type BreadcrumbTitles = Record<string, string>;

const BreadcrumbTitlesContext = createContext<{
  titles: BreadcrumbTitles;
  setTitle: (segment: string, title: string) => void;
}>({
  titles: {},
  setTitle: () => {},
});

export function BreadcrumbTitlesProvider({ children }: { children: ReactNode }) {
  const [titles, setTitles] = useState<BreadcrumbTitles>({});

  const setTitle = useCallback((segment: string, title: string) => {
    setTitles((prev) =>
      prev[segment] === title ? prev : { ...prev, [segment]: title },
    );
  }, []);

  const value = useMemo(() => ({ titles, setTitle }), [titles, setTitle]);

  return (
    <BreadcrumbTitlesContext.Provider value={value}>
      {children}
    </BreadcrumbTitlesContext.Provider>
  );
}

export function useBreadcrumbTitles(): BreadcrumbTitles {
  return useContext(BreadcrumbTitlesContext).titles;
}

/**
 * Render inside a detail page (or its layout) to register the real title for
 * its dynamic segment. Renders nothing. Safe to render from a server component
 * — it is a client boundary that only runs the registration effect.
 */
export function SetBreadcrumbTitle({
  segment,
  title,
}: {
  segment: string | null | undefined;
  title: string | null | undefined;
}) {
  const { setTitle } = useContext(BreadcrumbTitlesContext);

  useEffect(() => {
    if (segment && title) {
      setTitle(segment, title);
    }
  }, [segment, title, setTitle]);

  return null;
}
